// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * View Config Context
 *
 * Provides KB view configuration to child components.
 * Use with KnowledgeBaseProvider for full functionality.
 */
import { createContext, useContext, useMemo, type PropsWithChildren } from 'react'

import { FINDINGS_CONFIG, type KnowledgeBaseViewConfig } from '../types'

const ViewConfigContext = createContext<KnowledgeBaseViewConfig | null>(null)

interface ViewConfigProviderProps {
  config: KnowledgeBaseViewConfig
}

export function ViewConfigProvider({
  config,
  children,
}: PropsWithChildren<ViewConfigProviderProps>): JSX.Element {
  // Memoize to prevent unnecessary re-renders
  const value = useMemo(() => config, [config])

  return (
    <ViewConfigContext.Provider value={value}>
      {children}
    </ViewConfigContext.Provider>
  )
}

/**
 * Access the current KB view configuration.
 * Returns FINDINGS_CONFIG as fallback for backwards compatibility.
 */
export function useViewConfig(): KnowledgeBaseViewConfig {
  const context = useContext(ViewConfigContext)
  return context ?? FINDINGS_CONFIG
}
